import { useEffect, useRef, useState } from 'react';
import type { TicketMessage } from '../../types/tickets';
import { formatTicketDateTime } from '../../lib/ticketComposeHelpers';

type Props = {
  notes: TicketMessage[];
  busy?: boolean;
  startEditId?: string | null;
  onEditStarted?: () => void;
  onAdd: (text: string, files: File[]) => Promise<void>;
  onEdit: (noteId: string, text: string) => Promise<void>;
  onDelete: (noteId: string) => Promise<void>;
};

function noteText(msg: TicketMessage): string {
  const text = msg.body_text?.trim();
  if (text) return text;
  return (msg.body_html ?? '').replace(/<[^>]+>/g, ' ').replace(/\s+/g, ' ').trim();
}

function autoResizeTextarea(el: HTMLTextAreaElement | null) {
  if (!el) return;
  el.style.height = 'auto';
  const max = 120;
  el.style.height = `${Math.min(el.scrollHeight, max)}px`;
  el.style.overflowY = el.scrollHeight > max ? 'auto' : 'hidden';
}

export function TicketActivityBubble({
  notes,
  busy,
  startEditId,
  onEditStarted,
  onAdd,
  onEdit,
  onDelete,
}: Props) {
  const fileInputRef = useRef<HTMLInputElement>(null);
  const notesRef = useRef<HTMLDivElement>(null);
  const [draft, setDraft] = useState('');
  const [pendingFiles, setPendingFiles] = useState<File[]>([]);
  const [editingId, setEditingId] = useState<string | null>(null);
  const [editDraft, setEditDraft] = useState('');
  const [error, setError] = useState<string | null>(null);

  const ordered = [...notes].sort(
    (a, b) => new Date(a.sent_at).getTime() - new Date(b.sent_at).getTime(),
  );

  useEffect(() => {
    setEditingId(null);
    setEditDraft('');
    setError(null);
  }, [notes.length]);

  useEffect(() => {
    const el = notesRef.current;
    if (!el) return;
    el.scrollTop = el.scrollHeight;
  }, [notes.length]);

  useEffect(() => {
    if (!startEditId) return;
    const note = notes.find((n) => n.id === startEditId);
    if (!note) return;
    setEditingId(startEditId);
    setEditDraft(noteText(note));
    onEditStarted?.();
  }, [startEditId, notes, onEditStarted]);

  useEffect(() => {
    if (!editingId) return;
    window.requestAnimationFrame(() => {
      const el = document.querySelector(
        '.ticket-activity-bubble__edit',
      ) as HTMLTextAreaElement | null;
      autoResizeTextarea(el);
    });
  }, [editingId, editDraft]);

  function resetComposer() {
    setDraft('');
    setPendingFiles([]);
    if (fileInputRef.current) fileInputRef.current.value = '';
  }

  async function submitNew() {
    const text = draft.trim();
    if (!text && pendingFiles.length === 0) return;
    setError(null);
    try {
      await onAdd(text, pendingFiles);
      resetComposer();
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to save comment.');
    }
  }

  async function submitEdit(noteId: string) {
    const text = editDraft.trim();
    if (!text) return;
    setError(null);
    try {
      await onEdit(noteId, text);
      setEditingId(null);
      setEditDraft('');
    } catch (e) {
      setError(e instanceof Error ? e.message : 'Failed to update comment.');
    }
  }

  return (
    <div className="ticket-activity-bubble">
      <div className="ticket-activity-bubble__notes" ref={notesRef}>
        {ordered.length === 0 ? (
          <p className="ticket-activity-bubble__empty">No comments yet.</p>
        ) : (
          ordered.map((note) => (
            <article key={note.id} className="ticket-chat-note ticket-chat-note--panel">
              <div className="ticket-chat-note__bubble">
                {editingId === note.id ? (
                  <>
                    <textarea
                      className="field-input ticket-activity-bubble__edit"
                      rows={2}
                      value={editDraft}
                      disabled={busy}
                      ref={autoResizeTextarea}
                      onChange={(e) => {
                        setEditDraft(e.target.value);
                        autoResizeTextarea(e.target);
                      }}
                    />
                    <div className="ticket-activity-bubble__item-actions">
                      <button
                        type="button"
                        className="btn-primary btn-sm"
                        disabled={busy || !editDraft.trim()}
                        onClick={() => void submitEdit(note.id)}
                      >
                        Save
                      </button>
                      <button
                        type="button"
                        className="btn-secondary btn-sm"
                        disabled={busy}
                        onClick={() => {
                          setEditingId(null);
                          setEditDraft('');
                        }}
                      >
                        Cancel
                      </button>
                    </div>
                  </>
                ) : (
                  <>
                    <p className="ticket-chat-note__label">Internal note</p>
                    <p className="ticket-chat-note__text">{noteText(note)}</p>
                    <div className="ticket-chat-note__footer">
                      <p className="ticket-chat-note__meta">
                        {note.created_by_email || note.from_email || 'Agent'} ·{' '}
                        {formatTicketDateTime(note.sent_at)}
                      </p>
                      <div className="ticket-chat-note__actions">
                        <button
                          type="button"
                          className="ticket-mail__action-btn"
                          disabled={busy}
                          onClick={() => {
                            setEditingId(note.id);
                            setEditDraft(noteText(note));
                          }}
                        >
                          Edit
                        </button>
                        <button
                          type="button"
                          className="ticket-mail__action-btn ticket-mail__action-btn--danger"
                          disabled={busy}
                          onClick={() => {
                            if (!window.confirm('Delete this comment?')) return;
                            void onDelete(note.id);
                          }}
                        >
                          Delete
                        </button>
                      </div>
                    </div>
                  </>
                )}
              </div>
            </article>
          ))
        )}
      </div>

      <div className="ticket-activity-bubble__composer">
        <textarea
          className="field-input ticket-activity-bubble__textarea"
          rows={2}
          value={draft}
          disabled={busy}
          placeholder="Write an internal note for your team (not emailed to customer)…"
          ref={autoResizeTextarea}
          onChange={(e) => {
            setDraft(e.target.value);
            autoResizeTextarea(e.target);
          }}
        />

        {pendingFiles.length > 0 ? (
          <ul className="ticket-activity-bubble__file-list">
            {pendingFiles.map((file, index) => (
              <li key={`${file.name}-${index}`}>
                <span>{file.name}</span>
                <button
                  type="button"
                  className="ticket-mail__action-btn ticket-mail__action-btn--danger"
                  disabled={busy}
                  onClick={() => setPendingFiles((prev) => prev.filter((_, i) => i !== index))}
                >
                  Remove
                </button>
              </li>
            ))}
          </ul>
        ) : null}

        {error ? <p className="form-error">{error}</p> : null}

        <div className="ticket-activity-bubble__composer-actions">
          <input
            ref={fileInputRef}
            type="file"
            multiple
            className="ticket-activity-bubble__file-input"
            onChange={(e) => {
              const files = [...(e.target.files ?? [])];
              if (files.length > 0) {
                setPendingFiles((prev) => [...prev, ...files]);
              }
              e.target.value = '';
            }}
          />
          <button
            type="button"
            className="btn-secondary btn-sm"
            disabled={busy}
            onClick={() => fileInputRef.current?.click()}
          >
            Attach file
          </button>
          <button
            type="button"
            className="btn-primary btn-sm"
            disabled={busy || (!draft.trim() && pendingFiles.length === 0)}
            onClick={() => void submitNew()}
          >
            {busy ? 'Saving…' : 'Save internal note'}
          </button>
        </div>
      </div>
    </div>
  );
}
